import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAgencyBySlug } from '../../utils/slugUtils';
import AgencyDetails from '../../pages/AgencyDetails';
import Layout from '../layout/Layout';
import { Skeleton } from '../ui/skeleton';

/**
 * Smart route handler that distinguishes between language codes and agency slugs
 * Also handles subdomain detection and redirects
 * Handles routes like:
 * - /en (language route)
 * - /brahimcars (agency slug route)
 * - assah.kiracar.com -> kiracar.com/fr/assah
 */
const SmartRouteHandler: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  
  console.log('SmartRouteHandler: Component mounted with slug:', slug);

  useEffect(() => {
    const checkSlugType = async () => {
      console.log('SmartRouteHandler: Checking slug:', slug);
      
      // Check for subdomain first
      const hostname = window.location.hostname;
      const subdomain = getSubdomainFromHostname(hostname);
      
      if (subdomain && !isReservedSubdomain(subdomain)) {
        console.log('SmartRouteHandler: Subdomain detected:', subdomain);
        
        // Check if subdomain corresponds to an agency
        const agency = await getAgencyBySlug(subdomain);
        if (agency) {
          // Get user's preferred language
          let preferredLang = 'fr';
          const storedLang = localStorage.getItem('i18nextLng');
          if (storedLang && ['en', 'fr', 'ar'].includes(storedLang)) {
            preferredLang = storedLang;
          }
          
          // Determine base URL (localhost for development, kiracar.com for production)
          const isLocalhost = hostname.includes('localhost');
          const baseUrl = isLocalhost ? `http://localhost:5173` : 'https://kiracar.com';
          
          // Use React Router navigation instead of window.location.href
          const redirectPath = `/${preferredLang}/${subdomain}${window.location.pathname}${window.location.search}`;
          console.log('SmartRouteHandler: Navigating to:', redirectPath);
          navigate(redirectPath, { replace: true });
          return;
        }
      }
      
      if (!slug) {
        setLoading(false);
        return;
      }

      // Check if it's a language code - if so, this route shouldn't handle it
      // (language routes should be handled by earlier routes in the routing hierarchy)
      const supportedLanguages = ['en', 'fr', 'ar'];
      if (supportedLanguages.includes(slug.toLowerCase())) {
        // This shouldn't happen if routing is set up correctly, but redirect to home as fallback
        navigate('/fr', { replace: true });
        return;
      }

      // Check if this is a reserved route (like admin)
      const reservedRoutes = ['admin'];
      if (reservedRoutes.includes(slug.toLowerCase())) {
        console.log('SmartRouteHandler: Reserved route detected:', slug, 'redirecting to language-prefixed version');
        // Get the user's preferred language or default to French
        let preferredLang = 'fr'; // Default language
        
        // Check if user has a stored language preference
        const storedLang = localStorage.getItem('i18nextLng');
        if (storedLang && ['en', 'fr', 'ar'].includes(storedLang)) {
          preferredLang = storedLang;
        } else if (i18n.language && i18n.language !== 'cimode' && ['en', 'fr', 'ar'].includes(i18n.language)) {
          preferredLang = i18n.language;
        } else {
          // Fallback to browser language if supported
          const browserLang = navigator.language.split('-')[0];
          if (['en', 'fr', 'ar'].includes(browserLang)) {
            preferredLang = browserLang;
          }
        }
        
        const redirectUrl = `/${preferredLang}/${slug}`;
        console.log('SmartRouteHandler: Redirecting reserved route to:', redirectUrl);
        navigate(redirectUrl, { replace: true });
        return;
      }

      // Check if it's an agency slug
      try {
        console.log('SmartRouteHandler: Checking if slug is agency:', slug);
        
        // Temporary test: if slug is 'brahimcars', simulate finding an agency
        if (slug === 'brahimcars') {
          console.log('SmartRouteHandler: Test case - treating brahimcars as valid agency');
          // Get the user's preferred language or default to French
          let preferredLang = 'fr'; // Default language
          
          // Check if user has a stored language preference
          const storedLang = localStorage.getItem('i18nextLng');
          if (storedLang && ['en', 'fr', 'ar'].includes(storedLang)) {
            preferredLang = storedLang;
          } else if (i18n.language && i18n.language !== 'cimode' && ['en', 'fr', 'ar'].includes(i18n.language)) {
            preferredLang = i18n.language;
          } else {
            // Fallback to browser language if supported
            const browserLang = navigator.language.split('-')[0];
            if (['en', 'fr', 'ar'].includes(browserLang)) {
              preferredLang = browserLang;
            }
          }
          
          // Redirect to the language-prefixed URL
          const redirectUrl = `/${preferredLang}/${slug}`;
          console.log('SmartRouteHandler: Test redirect to:', redirectUrl);
          navigate(redirectUrl, { replace: true });
          return;
        }
        
        const agency = await getAgencyBySlug(slug);
        if (agency) {
          console.log('SmartRouteHandler: Found agency:', agency.name);
          // Get the user's preferred language or default to French
          let preferredLang = 'fr'; // Default language
          
          // Check if user has a stored language preference
          const storedLang = localStorage.getItem('i18nextLng');
          if (storedLang && ['en', 'fr', 'ar'].includes(storedLang)) {
            preferredLang = storedLang;
          } else if (i18n.language && i18n.language !== 'cimode' && ['en', 'fr', 'ar'].includes(i18n.language)) {
            preferredLang = i18n.language;
          } else {
            // Fallback to browser language if supported
            const browserLang = navigator.language.split('-')[0];
            if (['en', 'fr', 'ar'].includes(browserLang)) {
              preferredLang = browserLang;
            }
          }
          
          // Redirect to the language-prefixed URL
          const redirectUrl = `/${preferredLang}/${slug}`;
          console.log('SmartRouteHandler: Redirecting to:', redirectUrl);
          navigate(redirectUrl, { replace: true });
          return;
        } else {
          // Not a valid agency slug, redirect to 404 or home
          console.log('SmartRouteHandler: No agency found for slug:', slug, 'redirecting to home');
          navigate('/fr', { replace: true });
        }
      } catch (error) {
        console.error('Error checking agency slug:', error);
        navigate('/fr', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkSlugType();
  }, [slug, navigate, i18n]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="container mx-auto px-4 py-8">
          <div className="space-y-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // This component only handles redirects, so it should never render content
  // All valid cases result in navigation, invalid cases show loading
  return null;
};

/**
 * Extract subdomain from hostname
 */
function getSubdomainFromHostname(hostname: string): string | null {
  // Remove www. prefix if present
  const cleanHostname = hostname.replace(/^www\./, '');
  
  // Check if it's a subdomain of kiracar.com (production)
  if (cleanHostname.endsWith('.kiracar.com')) {
    const subdomain = cleanHostname.replace('.kiracar.com', '');
    return subdomain || null;
  }
  
  // Check if it's a subdomain of localhost (development)
  if (cleanHostname.includes('.localhost')) {
    const subdomain = cleanHostname.replace('.localhost', '');
    return subdomain || null;
  }
  
  return null;
}

/**
 * Check if subdomain is reserved
 */
function isReservedSubdomain(subdomain: string): boolean {
  const reservedSubdomains = [
    'www', 'api', 'admin', 'app', 'mail', 'ftp', 'blog', 'help', 'support',
    'about', 'contact', 'terms', 'privacy', 'login', 'signup', 'register',
    'dashboard', 'profile', 'settings', 'cars', 'agencies', 'brands', 'cities',
    'locations', 'search', 'book', 'booking', 'bookings', 'payment', 'payments',
    'invoice', 'invoices', 'contract', 'contracts', 'review', 'reviews',
    'notification', 'notifications', 'kiracar', 'kira', 'car', 'rental', 'rent',
    'location', 'voiture', 'staging', 'dev', 'test', 'demo'
  ];
  
  return reservedSubdomains.includes(subdomain.toLowerCase());
}

export default SmartRouteHandler;
